/* eslint-disable arrow-body-style */
// Global variable sent down for SpecificContent
/* global SpecificContent */

import { addToBucket } from 'es6/helpers/api';
import { camelCaseKeys } from 'es6/helpers/case';
import { StudySpecificContent } from 'es6/core/loaders';
import Logger from 'es6/services/logger';
import IKnowApp from 'es6/controllers/apps/iknow-app';
import ItemSummaryScreen from 'es6/components/apps/screens/item-summary';

export default class SpecificContentApp extends IKnowApp {
  constructor(...args) {
    super(...args);

    // Store the manipulated specific content data after it was added
    // to the goal.
    this._specificContent = {};
  }

  /**
   * Retrieves the cue language from the global namespace.
   *
   * @returns {String}
   */
  static getCueLanguage() {
    return SpecificContent.CueLanguage;
  }

  /**
   * Retrieves the response language from the global namespace.
   *
   * @returns {String}
   */
  static getResponseLanguage() {
    return SpecificContent.ResponseLanguage;
  }

  /**
   * Retrieves the bucket setting from the global namespace.
   *
   * @returns {String}
   */
  static getTargetGoal() {
    return SpecificContent.TargetGoal;
  }

  /**
   * Retrieves the specific content from the global namespace.
   *
   * @returns {Object[]}
   */
  static getSpecificContent() {
    return camelCaseKeys(SpecificContent.Content);
  }

  /**
   * Add the specific content to the user bucket before the start of
   * the session. This is to prevent the wrong courses from being
   * focused at the end of the session.
   *
   * @override
   */
  _fetchData(...args) {
    const goalTitle = SpecificContentApp.getTargetGoal();

    // Parameters for creating the goal if it is not found.
    const postParams = {
      cueLanguage: SpecificContentApp.getCueLanguage(),
      responseLanguage:  SpecificContentApp.getResponseLanguage(),
    };

    // Build parameters for sending to the create course API.
    const items = SpecificContentApp.getSpecificContent().map(gip => {
      return { goalId: gip.goalId, itemId: gip.contentId };
    });

    // No goal title to save to?
    if (!goalTitle) {
      Logger.error('No goal title provided');
      return;
    }

    // Add items to the bucket.
    addToBucket(goalTitle, postParams, items).then((goalId) => {
      // Build the parameters to be send to the specific content request.
      this._specificContent = SpecificContentApp.getSpecificContent().map(gip => {
        return { goalId, contentId: gip.contentId };
      });

      // No matter what, show the summary screen.
      super._fetchData(...args);
    }).catch(reason => {
      // Something failed with uploading.
      Logger.error('Failed to add items to goal.', reason);
      this._errorHandler(reason);
    });
  }

  /**
   * @override
   * @returns {Loader}
   */
  _getStudyLoader() {
    const params = this._getStudyParameters();
    return new StudySpecificContent(this._specificContent, params);
  }

  /**
   * For specific content, quitting the app should close the window if it was opened
   * in a pop up. Otherwise, it should go back to the page that performed the POST.
   *
   * @override
   */
  _quitSession() {
    if (window.opener) {
      window.close();
    } else {
      history.back();
    }
  }

  /**
   * For specific content, there is no such things as a course recommendation.
   *
   * @override
   * @returns {Promise}
   */
  _setupCourseRecommendation() {
    return Promise.resolve(false);
  }

  /**
   * Don't actually show a welcome screen. Start a new session.
   *
   * @override
   */
  _showWelcomeScreen() {
    this._startSession();
  }

  /**
   * Show the session item summary screen.
   *
   * @override
   * @param {Object[]} presentationResults  presentation results sent to server
   * @param {Object}   sessionResults       session results returned from server
   */
  _showSummaryScreen(presentationResults = [], sessionResults) {
    const memories = sessionResults && sessionResults.memories || [];

    // Only show item details that were returned from the server.
    const itemPackages = memories.map(m => this._sessionData.itemPackages[m.contentId]);

    // Method that gets executed when mastering an item package.
    const onComplete = (itemPackage) => {
      const onCompleted = this._showSummaryScreen.bind(this, presentationResults, sessionResults);
      this._completeItemPrompt(itemPackage, onCompleted);
    };

    this._screenManager.renderComponent(ItemSummaryScreen, {
      itemPackages,
      onComplete,
      onQuit: this._quitSession,
    });

    // Reset the banner.
    this._renderBanner({ onQuit: this._quitSession });
  }

  /**
   * There are no session configs. No reason to update/change them.
   *
   * @override
   * @returns {Boolean}
   */
  _updateSessionConfig() {
    return true;
  }
}
